import { createContext, useContext, useReducer, ReactNode, useRef, useCallback } from 'react';
import { AppState, AppAction, AppContextType, Vehicle } from '@/types';
import { useUndoRedo } from '@/hooks/useUndoRedo';

// Initial state
const initialState: AppState = {
  xmlDoc: null,
  vehicleData: new Map<string, Vehicle>(),
  allModelsByGroup: {
    pedGroups: [],
    vehGroups: []
  },
  hasUnsavedChanges: false,
  ui: {
    selectedGroupType: 'vehGroups',
    loadingStates: {
      xml: false,
      lua: false
    },
    errors: [],
    successMessages: [],
    searchQuery: '',
    canUndo: false,
    canRedo: false,
    globalCollapseTrigger: 0,
    globalExpandTrigger: 0,
    collapseAllMode: false
  },
  lastUpdate: Date.now()
};

// Reducer function
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_XML_DOC': {
      const extractedModels = extractModelsFromXML(action.payload);
      const xmlVehicles = createXMLOnlyVehicles(extractedModels);
      
      // Merge XML-only vehicles (but don't overwrite Lua data)
      const updatedVehicleMap = new Map<string, Vehicle>(state.vehicleData);
      xmlVehicles.forEach(vehicle => {
        // Only add XML-only data if no Lua data exists for this model
        // Lua data should always take precedence
        if (!updatedVehicleMap.has(vehicle.model) || updatedVehicleMap.get(vehicle.model)?.source !== 'lua') {
          updatedVehicleMap.set(vehicle.model, vehicle);
        }
      });
      
      return {
        ...state,
        xmlDoc: action.payload,
        vehicleData: updatedVehicleMap,
        allModelsByGroup: {
          ...state.allModelsByGroup,
          ...extractedModels
        },
        lastUpdate: Date.now()
      };
    }

    case 'UPDATE_XML_DOC': {
      return {
        ...state,
        xmlDoc: action.payload,
        lastUpdate: Date.now()
      };
    }

    case 'SET_VEHICLE_DATA': {
      // Create new vehicle map, prioritizing Lua data over existing XML-only data
      const vehicleMap = new Map<string, Vehicle>(state.vehicleData);
      
      action.payload.forEach(vehicle => {
        // Always prioritize Lua data - it will overwrite any existing XML-only data
        if (vehicle.source === 'lua') {
          vehicleMap.set(vehicle.model, vehicle);
        } else {
          // Only add XML-only data if no Lua data exists for this model
          if (!vehicleMap.has(vehicle.model) || vehicleMap.get(vehicle.model)?.source !== 'lua') {
            vehicleMap.set(vehicle.model, vehicle);
          }
        }
      });
      
      // Add Lua vehicles to vehGroups models
      const luaVehicles = action.payload
        .filter(v => v.source === 'lua')
        .map(v => v.model);
      const updatedVehGroups = [
        ...new Set([...state.allModelsByGroup.vehGroups, ...luaVehicles])
      ];

      return {
        ...state,
        vehicleData: vehicleMap,
        allModelsByGroup: {
          ...state.allModelsByGroup,
          vehGroups: updatedVehGroups
        }
      };
    }

    case 'MARK_UNSAVED':
      return {
        ...state,
        hasUnsavedChanges: true
      };

    case 'MARK_SAVED':
      return {
        ...state,
        hasUnsavedChanges: false
      };

    case 'ADD_MANUAL_VEHICLE': {
      const { model, groupType } = action.payload;

      // Check if vehicle already exists
      if (state.vehicleData.has(model)) {
        return state;
      }

      // Create manual vehicle entry
      const manualVehicle: Vehicle = {
        name: model,
        brand: '',
        model: model,
        price: 0,
        category: '',
        type: '',
        hash: '',
        source: 'manual'
      };

      // Add to vehicle data
      const updatedVehicleMap = new Map<string, Vehicle>(state.vehicleData);
      updatedVehicleMap.set(model, manualVehicle);

      // Add to the appropriate group
      const updatedGroups = [...state.allModelsByGroup[groupType], model];

      return {
        ...state,
        vehicleData: updatedVehicleMap,
        allModelsByGroup: {
          ...state.allModelsByGroup,
          [groupType]: updatedGroups
        },
        lastUpdate: Date.now()
      };
    }

    case 'SET_GROUP_TYPE':
      return {
        ...state,
        ui: {
          ...state.ui,
          selectedGroupType: action.payload
        }
      };

    case 'SET_LOADING':
      return {
        ...state,
        ui: {
          ...state.ui,
          loadingStates: {
            ...state.ui.loadingStates,
            [action.payload.type]: action.payload.loading
          }
        }
      };

    case 'ADD_ERROR':
      return {
        ...state,
        ui: {
          ...state.ui,
          errors: [...state.ui.errors, action.payload]
        }
      };

    case 'CLEAR_ERRORS':
      return {
        ...state,
        ui: {
          ...state.ui,
          errors: []
        }
      };

    case 'ADD_SUCCESS':
      return {
        ...state,
        ui: {
          ...state.ui,
          successMessages: [...state.ui.successMessages, action.payload]
        }
      };

    case 'CLEAR_SUCCESS':
      return {
        ...state,
        ui: {
          ...state.ui,
          successMessages: []
        }
      };

    case 'FORCE_UPDATE':
      return {
        ...state,
        lastUpdate: Date.now()
      };

    case 'SET_SEARCH_QUERY':
      return {
        ...state,
        ui: {
          ...state.ui,
          searchQuery: action.payload
        }
      };

    case 'JUMP_TO_STATE':
    case 'REDO':
      // Undo/redo state is managed in the provider
      // Just return the state as-is, the provider will update it
      return state;

    case 'UPDATE_UNDO_REDO_STATE':
      return {
        ...state,
        ui: {
          ...state.ui,
          canUndo: action.payload.canUndo,
          canRedo: action.payload.canRedo
        }
      };

    case 'TRIGGER_COLLAPSE_ALL':
      return {
        ...state,
        ui: {
          ...state.ui,
          globalCollapseTrigger: state.ui.globalCollapseTrigger + 1,
          collapseAllMode: true
        }
      };

    case 'TRIGGER_EXPAND_ALL':
      return {
        ...state,
        ui: {
          ...state.ui,
          globalExpandTrigger: state.ui.globalExpandTrigger + 1,
          collapseAllMode: false
        }
      };

    default:
      return state;
  }
}

// Helper function to extract models from XML
function extractModelsFromXML(xmlDoc: Document): { pedGroups: string[]; vehGroups: string[] } {
  const result = { pedGroups: [] as string[], vehGroups: [] as string[] };
  
  (['pedGroups', 'vehGroups'] as const).forEach(groupType => {
    // Handle both Name and n elements (different XML formats)
    const nameElements = Array.from(xmlDoc.querySelectorAll(`${groupType} > Item > models > Item > Name`));
    const nElements = Array.from(xmlDoc.querySelectorAll(`${groupType} > Item > models > Item > n`));
    
    const models = [...nameElements, ...nElements]
      .map(node => node.textContent || '')
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index);
    
    result[groupType] = models;
  });
  
  return result;
}

// Helper function to create XML-only vehicle entries
function createXMLOnlyVehicles(modelsByGroup: { pedGroups: string[]; vehGroups: string[] }): Vehicle[] {
  const xmlVehicles: Vehicle[] = [];
  
  // Create XML-only vehicle entries for all models found in XML
  [...modelsByGroup.pedGroups, ...modelsByGroup.vehGroups].forEach(model => {
    xmlVehicles.push({
      name: model,
      brand: '',
      model: model,
      price: 0,
      category: '',
      type: '',
      hash: '',
      source: 'xml'
    });
  });
  
  return xmlVehicles;
}

// Create context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider component
interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const undoRedo = useUndoRedo();
  const isUndoingRef = useRef(false);

  // Handle undo action
  const handleUndo = useCallback(() => {
    if (isUndoingRef.current) return;
    
    const previousDoc = undoRedo.undo();
    if (previousDoc) {
      isUndoingRef.current = true;
      dispatch({ type: 'SET_XML_DOC', payload: previousDoc });
      // Update undo/redo state
      setTimeout(() => {
        isUndoingRef.current = false;
        dispatch({ type: 'UPDATE_UNDO_REDO_STATE', payload: {
          canUndo: undoRedo.canUndo(),
          canRedo: undoRedo.canRedo()
        }});
      }, 0);
    }
  }, [undoRedo, dispatch]);

  // Handle redo action
  const handleRedo = useCallback(() => {
    if (isUndoingRef.current) return;
    
    const nextDoc = undoRedo.redo();
    if (nextDoc) {
      isUndoingRef.current = true;
      dispatch({ type: 'SET_XML_DOC', payload: nextDoc });
      // Update undo/redo state
      setTimeout(() => {
        isUndoingRef.current = false;
        dispatch({ type: 'UPDATE_UNDO_REDO_STATE', payload: {
          canUndo: undoRedo.canUndo(),
          canRedo: undoRedo.canRedo()
        }});
      }, 0);
    }
  }, [undoRedo, dispatch]);

  // Handle jump to state action
  const handleJumpToState = useCallback((targetIndex: number) => {
    if (isUndoingRef.current) return;
    
    const targetDoc = undoRedo.jumpToState(targetIndex);
    if (targetDoc) {
      isUndoingRef.current = true;
      dispatch({ type: 'SET_XML_DOC', payload: targetDoc });
      // Update undo/redo state
      setTimeout(() => {
        isUndoingRef.current = false;
        dispatch({ type: 'UPDATE_UNDO_REDO_STATE', payload: {
          canUndo: undoRedo.canUndo(),
          canRedo: undoRedo.canRedo()
        }});
      }, 0);
    }
  }, [undoRedo, dispatch]);

  // Override dispatch to handle undo/redo and track state
  const enhancedDispatch = useCallback((action: AppAction) => {
    if (action.type === 'UNDO') {
      handleUndo();
    } else if (action.type === 'REDO') {
      handleRedo();
    } else if (action.type === 'JUMP_TO_STATE') {
      handleJumpToState(action.payload);
    } else {
      // For SET_XML_DOC, push the new state to history after reducer processes it
      if ((action.type === 'SET_XML_DOC' || action.type === 'UPDATE_XML_DOC') && !isUndoingRef.current) {
        if (action.type === 'SET_XML_DOC' && !state.xmlDoc) {
          undoRedo.clearHistory();
        }
        // Get the new state by running the reducer
        const newState = appReducer(state, action);
        // Push the new state to history (description will be calculated internally)
        if (newState.xmlDoc) {
          undoRedo.pushState(newState.xmlDoc);
        }
      }
      dispatch(action);
      // Update undo/redo state after any action
      setTimeout(() => {
        dispatch({ type: 'UPDATE_UNDO_REDO_STATE', payload: {
          canUndo: undoRedo.canUndo(),
          canRedo: undoRedo.canRedo()
        }});
      }, 0);
    }
  }, [handleUndo, handleRedo, handleJumpToState, dispatch, state, undoRedo]);

  return (
    <AppContext.Provider value={{ state, dispatch: enhancedDispatch, getHistory: undoRedo.getHistory, getCurrentIndex: undoRedo.getCurrentIndex }}>
      {children}
    </AppContext.Provider>
  );
}

// Custom hook to use the context
/* eslint-disable react-refresh/only-export-components */
export function useAppContext(): AppContextType {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
}
/* eslint-enable react-refresh/only-export-components */
